import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Measurement from "./Measurement";
import InitContext from "../pages/InitContext";

import {
  faChartLine,
  faPlus,
  faSortDown,
  faSortUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import MeasurementsLineChart from "./MeasurementsLineChart";
import {formatDateTime, sortData} from "../pages/utils";
import ReactDOM from "react-dom";
import Select from "react-select";
import {Consts} from "../pages/consts";

const MeasurementsTable = ({
  children,
  selectedPatient,
  hcpView,
  printView = false,
  chartActionInToolbar = false,
}) => {
  const { t } = useTranslation();
  const [measurements, setMeasurements] = useState([]);
  const [addMeasurement, setAddMeasurement] = useState(false);
  const context = useContext(InitContext);

  const { loading, error, clearError, getDbMeasurements, deleteDbMeasurement } =
    useMmcService();

  const [showMeasurementsLineChart, setShowMeasurementsLineChart] =
    useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.DATE_TIME,
    ascending: false,
  });

  const [sortedMeasurements, setSortedMeasurements] = useState(
    measurements ? [...measurements] : [],
  );

  const sortOptions = [
    { value: Consts.DATE_TIME, label: t("measurement_date") },
    { value: Consts.NAME, label: t("measurement_name") },
  ];

  const toSortableMeasurementDate = (dateValue) => {
    if (!dateValue) {
      return null;
    }

    if (dateValue instanceof Date) {
      return dateValue;
    }

    const raw = String(dateValue).trim();

    // dd.MM.yyyy or dd.MM.yyyy HH:mm
    const dotMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);

    if (dotMatch) {
      const [, day, month, year, hour = "00", minute = "00"] = dotMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
    }

    // dd/MM/yyyy or dd/MM/yyyy HH:mm
    const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);

    if (slashMatch) {
      const [, day, month, year, hour = "00", minute = "00"] = slashMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed?.getTime()) ? null : parsed;
  };

  const getSortableValue = (item, sortKey) => {
    if (sortKey === Consts.DATE_TIME) {
      return toSortableMeasurementDate(item?.dateTime);
    }

    return item?.[sortKey];
  };

  useEffect(() => {
    const sorted = sortData(
      sortConfig?.key,
      measurements,
      sortConfig?.ascending,
      (item) => getSortableValue(item, sortConfig?.key),
    );

    setSortedMeasurements(sorted);
  }, [measurements, sortConfig]);

  const handleSortChange = (key) => {
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;
    setSortConfig({ key, ascending });
  };

  const patientId = !selectedPatient ? context?.patient?.id : selectedPatient?.id;

  useEffect(() => {
    clearError();

    if (patientId) {
      getDbMeasurements(context?.token, patientId).then((measurements) =>
        handleMeasurementsFromDb(measurements),
      );
    }
  }, [printView, patientId]);

  const handleMeasurementsFromDb = (measurements) => {
    if (measurements) {
      setMeasurements(
        measurements?.filter((measurement) => measurement?.params?.length > 0),
      );
    }
  };

  const handleDeleteMeasurement = (measurement) => {
    clearError();

    const newMeasurements = measurements?.filter(function (loopMeasurement) {
      if (measurement?.id) {
        return loopMeasurement?.id !== measurement?.id;
      } else {
        return (
          loopMeasurement?.dateTime !== measurement?.dateTime &&
          loopMeasurement?.name !== measurement?.name
        );
      }
    });

    deleteDbMeasurement(context?.token, measurement?.id).then(() =>
      setMeasurements(newMeasurements),
    );
  };

  const addNewMeasurement = () => {
    setAddMeasurement(true);
  };

  const handleSaveNewMeasurement = (measurement) => {
    if (measurement) {
      measurement = {
        ...measurement,
        dateTime: formatDateTime(measurement.dateTime + " 02:00:00", false),
      };

      const updatedMeasurements = [...measurements, measurement];

      const resortedMeasurements = sortData(
        sortConfig?.key,
        updatedMeasurements,
        sortConfig?.ascending,
        (item) => getSortableValue(item, sortConfig?.key),
      );

      setMeasurements(updatedMeasurements);
      setSortedMeasurements(resortedMeasurements);
    }

    setAddMeasurement(false);
  };

  const handleCloseMeasurementsLineChat = () => {
    setShowMeasurementsLineChart(false);
  };

  let message =
    error && error?.includes(Consts.DELETE)
      ? t("error_by_deleting_a_measurement")
      : t("error_by_retrieving_measurements");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = !printView && loading && <Spinner />;

  return (
    <>
      {addMeasurement &&
        ReactDOM.createPortal(
          <Measurement
            selectedPatient={selectedPatient}
            onSaveNewMeasurement={handleSaveNewMeasurement}
            showDialog={addMeasurement}
            hcpView={hcpView}
          />,
          document.getElementById(Consts.ROOT),
        )}
      {showMeasurementsLineChart &&
        ReactDOM.createPortal(
          <MeasurementsLineChart
            measurements={measurements}
            onCloseDialog={handleCloseMeasurementsLineChat}
            showDialog={showMeasurementsLineChart}
            patientId={patientId}
          />,
          document.getElementById(Consts.ROOT),
        )}
      {!printView && (
        <>
          {(children || !chartActionInToolbar) && (
            <header className="w3-container w3-center w3-padding-small">
              <div className="w3-display-container w3-padding-small">
                {children}
                {!chartActionInToolbar && measurements?.length > 0 && (
                  <div className="w3-display-right">
                    <button
                      type="button"
                      className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary"
                      onClick={() => setShowMeasurementsLineChart(true)}
                      aria-label={t("show_chart", { defaultValue: "Show chart" })}
                    >
                      <FontAwesomeIcon icon={faChartLine} />
                    </button>
                  </div>
                )}
              </div>
            </header>
          )}
          {errorMessage}
          {spinner}
          <div className="w3-row w3-padding-small drugs-sort-row symptom-sort-row">
            <div className="w3-col s8">
              {sortedMeasurements?.length > 0 ? (
                <div className="symptom-sort-left-group">
                  <label className="drugs-sort-label symptom-sort-label">
                    {t("sort_by")}:
                  </label>
                  <div className="flex-aligned-items symptom-sort-controls">
                    <Select
                      onChange={(selectedOption) =>
                        handleSortChange(selectedOption?.value)
                      }
                      value={sortOptions?.find(
                        (option) => option?.value === sortConfig?.key,
                      )}
                      options={sortOptions}
                      placeholder={t("select")}
                      className="custom-select meds-sort-select symptom-sort-select"
                      classNamePrefix="meds-sort-select"
                      isSearchable={false}
                    />
                    <button
                      type="button"
                      className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary symptom-sort-order-button"
                      onClick={() => handleSortChange(sortConfig?.key)}
                      aria-label={t("toggle_sort_direction", { defaultValue: "Change sorting direction" })}
                    >
                      <FontAwesomeIcon
                        icon={sortConfig?.ascending ? faSortUp : faSortDown}
                        className="drugs-sort-icon symptom-sort-order-icon"
                      />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w3-container"></div>
              )}
            </div>
            { ( context?.role === Consts.HCP_PRESCRIBER || context?.role === Consts.PATIENT ) &&
              <div className="w3-col s4 w3-right-align symptom-sort-action-col">
                <div className="symptom-toolbar-actions">
                  {chartActionInToolbar && measurements?.length > 0 && (
                    <button
                      type="button"
                      className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary"
                      onClick={() => setShowMeasurementsLineChart(true)}
                      aria-label={t("show_chart", { defaultValue: "Show chart" })}
                    >
                      <FontAwesomeIcon icon={faChartLine} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addNewMeasurement}
                    className="symptom-ui-button symptom-ui-button--primary responsive-button"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span className="symptom-ui-button__label">{t("add_new_measurement")}</span>
                  </button>
                </div>
              </div>
            }
          </div>
        </>
      )}
      <div
        className={
          printView
            ? "w3-container w3-left-align"
            : "w3-container form-content-scroll w3-left-align"
        }
      >
        {sortedMeasurements?.length > 0 ? (
          <div className={printView ? "w3-container" : "w3-container symptom-cards-surface"}>
            {sortedMeasurements?.map((item, i) => {
              return (
                <div
                  className="w3-column w3-card w3-margin symptom-table-card"
                  key={`measurementParam${i}`}
                >
                  <div className="w3-row">
                    <div className="w3-col s9 w3-mobile">
                      <label>
                        {item?.dateTime}: {t(item?.name)}
                      </label>
                    </div>
                    <div className="w3-col s3 w3-mobile">
                      {(( context?.role === Consts.HCP_PRESCRIBER || context?.role === Consts.PATIENT ) && !printView) && (
                        <div className="symptom-action-buttons">
                          <button
                            type="button"
                            onClick={() => handleDeleteMeasurement(item)}
                            className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--danger"
                            aria-label={t("delete", { defaultValue: "Delete" })}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {item?.params?.length > 0 &&
                    item?.params?.map((itemInner, k) => {
                      return (
                        <div className="w3-row w3-padding-small" key={k}>
                          <label className="bold-label">
                            {t(itemInner?.name)}:
                          </label>
                          <label className="label-margin-left">
                            {itemInner?.value}{" "}
                            {itemInner?.unit && t(itemInner?.unit)}
                          </label>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w3-container w3-center">
            <label>
              {selectedPatient ? t("not_available") : t("you_have_not_yet_entered_any_measurements")}
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default MeasurementsTable;
